import { BASE_URL } from './config'

async function fetchWithHandling(url: string, options: RequestInit) {
  const response = await fetch(url, options)
  if (!response.ok) {
    let errorMessage = 'An error occurred'
    try {
      const errorData = await response.json()
      errorMessage = errorData.message || errorMessage
    } catch (e) {
      errorMessage = response.statusText
    }
    throw new Error(errorMessage)
  }
  return response.json()
}

export async function getSessions(
  token: string,
  filters?: { from?: string; to?: string },
) {
  let url = `${BASE_URL}/api/v1/sessions`
  if (filters) {
    const params = new URLSearchParams()
    if (filters.from) params.append('from', filters.from)
    if (filters.to) params.append('to', filters.to)
    const queryString = params.toString()
    if (queryString) url += `?${queryString}`
  }
  return fetchWithHandling(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

export async function getSession(token: string, id: string) {
  return fetchWithHandling(`${BASE_URL}/api/v1/sessions/${id}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

export async function getReadings(
  token: string,
  sessionId: string,
  filters?: {
    from?: string
    to?: string
    sensorType?: string
    limit?: number
    offset?: number
  },
) {
  let url = `${BASE_URL}/api/v1/sessions/${sessionId}/readings`
  if (filters) {
    const params = new URLSearchParams()
    if (filters.from) params.append('from', filters.from)
    if (filters.to) params.append('to', filters.to)
    if (filters.sensorType) params.append('sensorType', filters.sensorType)
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.offset) params.append('offset', filters.offset.toString())
    const queryString = params.toString()
    if (queryString) url += `?${queryString}`
  }
  return fetchWithHandling(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

/**
 * Raw stored waveform segments for a session.
 *
 * Unlike the live WebSocket feed (which sends decoded numeric arrays), this
 * returns base64-encoded little-endian int16 samples plus the segment's own
 * `rateHz` and `scale`; callers must decode client-side.
 *
 * `limit`/`offset` are forwarded only when supplied. Whether the endpoint
 * honours them is detected from the response shape at call time rather than
 * assumed.
 */
export async function getSessionWaveform(
  token: string,
  sessionId: string,
  filters?: { from?: string; to?: string; limit?: number; offset?: number },
) {
  let url = `${BASE_URL}/api/v1/sessions/${sessionId}/waveform`
  if (filters) {
    const params = new URLSearchParams()
    if (filters.from) params.append('from', filters.from)
    if (filters.to) params.append('to', filters.to)
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.offset) params.append('offset', filters.offset.toString())
    const queryString = params.toString()
    if (queryString) url += `?${queryString}`
  }
  return fetchWithHandling(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

/**
 * @deprecated Does not run through the server's shared session lifecycle, so it
 * never publishes a WebSocket event and the belt keeps streaming.
 * Use `stopSession` instead.
 */
export async function endSession(token: string, id: string) {
  return fetchWithHandling(`${BASE_URL}/api/v1/sessions/${id}/end`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
}

/**
 * Stops the caller's active session. The endpoint resolves the active device
 * assignment server-side, so no body is required.
 */
export async function stopSession(token: string) {
  return fetchWithHandling(`${BASE_URL}/api/v1/sessions/stop`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
}
